import {
  buildSecureAppSearchParams,
  buyNowQueryParamsSessionStorage,
  CHANGE_PAYMENT_PLAN_BASE_URL,
  envs,
  navigationApi,
  PAYFLOW_BASE_URL,
  SECURE_BASE_URL,
  BILLING_BASE_URL,
  urlParamKeys,
} from '@ctct/infrastructure-layer';
import { observabilityAppService } from '@ctct/myaccount-utils-static';
import type { SelectedAddOn } from '@ctct/state-client-layer';

import { myAccountAppPaths } from '../constants';
import type { Flags, Router } from '../types';

const isSecure = envs.isSecure();
const isBilling = window.location.pathname.includes('pages/myaccount/billing');

export class MyAccountRouterService {
  constructor(private router: Router, private flags: Flags = {}) {}

  public api() {
    return {
      goToBuyNow: this.goToBuyNow.bind(this),
      goToBuyNowThankYou: this.goToBuyNowThankYou.bind(this),
      goToPayflowEntry: this.goToPayflowEntry.bind(this),
      // TODO - this was added to quickly fix the issue with the buy now redirect
      goToPayflowEntryFromBuyNow: this.goToPayflowEntryFromBuyNow.bind(this),
      goToFosPricingV2: this.goToFosPricingV2.bind(this),
      goToAddOnsStep: this.goToAddOnsStep.bind(this),
      goToCheckoutStep: this.goToCheckoutStep.bind(this),
      goToPaymentPlanStep: this.goToPaymentPlanStep.bind(this),
      goToPayflowPaymentPlanSelectStep: this.goToPayflowPaymentPlanSelectStep.bind(this),
      goToChangePaymentPlanStep: this.goToChangePaymentPlanStep.bind(this),
      goToPaymentPlanCheckoutStep: this.goToPaymentPlanCheckoutStep.bind(this),
      goToPayflowThankYou: this.goToPayflowThankYou.bind(this),
      goToVision6SignUp: () => navigationApi.goToVision6Signup(),
      getRoute: this.getRoute.bind(this),
      convertStoreValuesToUrlParams: ({
        selectedEmailPlanId,
        paymentPeriod: prepay,
        selectedContactTierMin,
        isNonProfit,
        promoCodes,
        selectedAddOns = [],
        returnUrl,
        returnLabel,
        skipParams = [],
        isSmsBundle = false,
      }: {
        selectedEmailPlanId: number | null;
        paymentPeriod: string;
        selectedContactTierMin: string;
        isNonProfit: boolean;
        promoCodes: string[];
        selectedAddOns?: SelectedAddOn[];
        returnUrl?: string | null;
        returnLabel?: string | null;
        skipParams?: string[];
        isSmsBundle?: boolean;
      }) => {
        const searchParams = new URLSearchParams();

        searchParams.append('paymentPeriod', String(prepay));
        searchParams.append('contactCount', selectedContactTierMin);

        if (isNonProfit) {
          searchParams.append('nonprofit', String(isNonProfit));
        }

        if (selectedEmailPlanId) {
          searchParams.append('emailPlanId', String(selectedEmailPlanId));
        }

        try {
          searchParams.append('addons', JSON.stringify(selectedAddOns));
        } catch (error) {
          observabilityAppService.notify(error, { message: 'Failed to stringify selectedAddOns', selectedAddOns });
        }

        promoCodes.forEach((code) => searchParams.append('promoCode', code));

        if (returnUrl) {
          searchParams.append('returnurl', String(returnUrl));
        }

        if (returnLabel) {
          searchParams.append('returnLabel', String(returnLabel));
        }

        if (isSmsBundle) {
          searchParams.set(urlParamKeys.affSmsBundle, 'true');
        }

        // Remove any parameters that should be skipped
        skipParams.forEach((param) => searchParams.delete(param));

        return searchParams.toString();
      },
    };
  }

  private getRoute() {
    return this.router.history.location.pathname;
  }

  // PAYFLOW
  private goToPayflowEntry({
    replace,
    keepParams,
    urlParams,
  }: { replace?: true; keepParams?: boolean; urlParams?: string } = {}) {
    if (isSecure || isBilling) {
      window.location.href = `${PAYFLOW_BASE_URL}/plans-and-pricing?${urlParams}`;

      return;
    }

    if (replace) {
      this.replace(myAccountAppPaths.payflowV2Entry, undefined, keepParams);
    } else {
      this.goTo(myAccountAppPaths.payflowV2Entry, undefined, keepParams);
    }
  }

  private goToPayflowEntryFromBuyNow({ replace }: { replace?: true } = {}) {
    if (isSecure || isBilling) {
      window.location.href = `${PAYFLOW_BASE_URL}/plans-and-pricing`;

      return;
    }

    if (replace) {
      this.replace(myAccountAppPaths.payflowEntry, undefined, true);
    } else {
      this.goTo(myAccountAppPaths.payflowEntry, undefined, true);
    }
  }

  private goToAddOnsStep({ keepParams, urlParams }: { keepParams?: boolean; urlParams?: string } = {}) {
    if (isSecure || isBilling) {
      window.location.href = `${PAYFLOW_BASE_URL}/add-ons?${urlParams}`;
    } else {
      this.goTo(myAccountAppPaths.addOns, undefined, keepParams);
    }
  }

  private goToPaymentPlanStep({ keepParams, urlParams }: { keepParams?: boolean; urlParams?: string } = {}) {
    if (isSecure || isBilling) {
      window.location.href = `${PAYFLOW_BASE_URL}/payment-plan-and-discounts?${urlParams}`;
    } else {
      this.goTo(myAccountAppPaths.paymentPlan, undefined, keepParams);
    }
  }

  private goToPayflowPaymentPlanSelectStep({
    keepParams,
    urlParams,
  }: { keepParams?: boolean; urlParams?: string } = {}) {
    if (isSecure || isBilling) {
      window.location.href = `${PAYFLOW_BASE_URL}/payment-plan/select?${urlParams}`;
    } else {
      this.goTo(`${PAYFLOW_BASE_URL}/payment-plan/select`, undefined, keepParams);
    }
  }

  private goToChangePaymentPlanStep({ keepParams, urlParams }: { keepParams?: boolean; urlParams?: string } = {}) {
    if (isSecure || isBilling) {
      window.location.href = `${CHANGE_PAYMENT_PLAN_BASE_URL}/select?${urlParams}`;
    } else {
      this.goTo(`${CHANGE_PAYMENT_PLAN_BASE_URL}/select`, undefined, keepParams);
    }
  }

  private goToPaymentPlanCheckoutStep({ urlParams }: { urlParams?: string } = {}) {
    const searchParams = buildSecureAppSearchParams(urlParams, this.flags);

    window.location.href = `${
      this.flags.isRedirectFromSecureEnabled ? BILLING_BASE_URL : SECURE_BASE_URL
    }/payment-plan/checkout?${searchParams.toString()}`;
  }

  private goToCheckoutStep({ urlParams }: { urlParams?: string } = {}) {
    const searchParams = buildSecureAppSearchParams(urlParams, this.flags);

    window.location.href = `${
      this.flags.isRedirectFromSecureEnabled ? BILLING_BASE_URL : SECURE_BASE_URL
    }/purchase/checkout?${searchParams.toString()}`;
  }

  private goToPayflowThankYou(firstName: string) {
    if (isSecure || isBilling) {
      this.goTo('confirmation', { firstName }, true);
    } else {
      this.goTo(myAccountAppPaths.confirmation, { firstName });
    }
  }

  // BUY NOW
  private goToBuyNow() {
    this.goTo(myAccountAppPaths.buyNow);
  }

  private goToBuyNowThankYou(firstName: string) {
    buyNowQueryParamsSessionStorage.clear();

    if (isSecure || isBilling) {
      this.goTo('buynow/thank-you', { firstName });
    } else {
      this.goTo(myAccountAppPaths.buyNowThankYou, { firstName });
    }
  }

  private goToFosPricingV2() {
    navigationApi.goToPricingPlans();
  }

  // helpers
  private goTo = (path: string, state?: unknown, keepParams?: boolean) => {
    if (keepParams) {
      const { search } = this.router.history.location;

      this.router.history.push({
        pathname: path,
        search,
        state,
      });
    } else {
      this.router.history.push(path, state);
    }
  };

  private replace = (path: string, state?: unknown, keepParams?: boolean) => {
    if (keepParams) {
      const { search } = this.router.history.location;

      this.router.history.replace({
        pathname: path,
        search,
        state,
      });
    } else {
      this.router.history.replace(path, state);
    }
  };
}
